/**
 * [INPUT]: 依赖 ./types ./geometry ./geo-features ./confidence，依赖 geo_norm_params 主类权重与 rarity 参数
 * [OUTPUT]: 对外提供 scoreFromLandmarks(ScorerInput)→ScoreReport、tierOf(total)→{tier,color}、SUBS 子指标定义
 * [POS]: apps/web/src/scorer 的编排层，串联 metrics→子分→主类→base→协同 bonus→total→tier→报告；纯函数，被 apps/web pipeline 调用
 * [PROTOCOL]: 变更时更新此头部，然后检查 ../CLAUDE.md
 *
 * 严苛 PSL 映射模型（rules_v0，2026-07-14 重做）：
 *  - 子分 sub ∈ [0, peak]（peak<1，默认 0.55）："落入理想带"不再等价满分，普通脸不再集体顶满
 *  - base = Σ w·main（通常 ≤5）
 *  - coherence_bonus = bonus_max · (n_near_peak/8)² · (1 − n_terrible/8)：8 子同时接近 peak 才显著，
 *    任意严重缺陷侵蚀 bonus —— 制造顶端陡崖，7+ 稀有
 *  - deltas 为反事实基线（相对 pop_avg 人群典型值），Σ impact = base − baseline_base，自洽
 *  - 删除旧 calibrateToStrictPsl（其顶端斜率>1 反向放大，已废弃）
 */
import type { Delta, GeoMetricKey, MainCategory, ScoreReport, ScorerInput } from './types'
import { clamp01, mean } from './geometry'
import { computeMetrics, PARAMS, scoreMetric } from './geo-features'
import { DEFAULT_PEAK } from './piecewise'
import { confidence } from './confidence'

/** 子指标定义：归属主类 + 数据来源（geo 指标或 photo_quality） */
export interface SubDef {
  key: string
  label: string
  main: MainCategory
  sources: Array<GeoMetricKey | 'photo_quality'>
}

/** 8 子指标 → 4 主类映射，对齐 docs §3.1 */
export const SUBS: SubDef[] = [
  { key: 'harmony_thirds', label: 'Facial Thirds', main: 'harmony', sources: ['facial_thirds'] },
  { key: 'harmony_midface', label: 'Midface Ratio', main: 'harmony', sources: ['midface_ratio', 'interpupillary'] },
  { key: 'harmony_symmetry', label: 'Overall Symmetry', main: 'harmony', sources: ['symmetry_index'] },
  { key: 'dimorphism_canthal', label: 'Canthal Tilt', main: 'dimorphism', sources: ['canthal_tilt'] },
  { key: 'dimorphism_jawlip', label: 'Jaw Definition', main: 'dimorphism', sources: ['bigonial_bizy'] },
  { key: 'angularity_gonial', label: 'Gonial Angle', main: 'angularity', sources: ['gonial_angle'] },
  { key: 'angularity_jaw', label: 'Jaw Sharpness', main: 'angularity', sources: ['jaw_sharpness'] },
  { key: 'presentation_quality', label: 'Presentation Quality', main: 'presentation', sources: ['photo_quality'] },
]

/** 弱项 → 自然手段建议模板（聚焦自然手段，避免人身攻击；T3 文案层可增强） */
export const SUGGESTIONS: Record<string, string> = {
  harmony_thirds: 'Facial third proportions can be visually adjusted through hairstyle (bangs / exposed forehead) and shooting at eye level.',
  harmony_midface: 'Midface ratio is affected by camera distance — shoot straight-on at eye level and avoid close-up wide-angle distortion.',
  harmony_symmetry: 'Mild asymmetry is often caused by posture or lighting — face the camera directly and use even bilateral lighting.',
  dimorphism_canthal: 'Canthal tilt is largely structural; eye makeup can enhance the visual lift of the outer corners.',
  dimorphism_jawlip: 'Jaw width is influenced by the masseter muscle — avoid excessive chewing; hairstyle can help frame the jawline.',
  angularity_gonial: 'Gonial angle definition can be enhanced through body fat management and masseter relaxation.',
  angularity_jaw: 'Jawline sharpness can be improved through body fat management and neck posture.',
  presentation_quality: 'Maintain good skincare, ensure ample lighting, and keep the camera lens clean and properly focused.',
}

interface TierDef {
  max: number
  tier: string
  color: string
}

const TIERS: TierDef[] = [
  { max: 1, tier: 'Subhuman', color: 'tier-gray' },
  { max: 2, tier: 'Below Average', color: 'tier-slate' },
  { max: 3.5, tier: 'Normie', color: 'tier-blue' },
  { max: 5, tier: 'High Normie', color: 'tier-cyan' },
  { max: 6, tier: 'HTN', color: 'tier-green' },
  { max: 6.8, tier: 'Chadlite', color: 'tier-yellow' },
  { max: 7.4, tier: 'Chad', color: 'tier-orange' },
  { max: Infinity, tier: 'Adam / Eve', color: 'tier-red' },
]

export function tierOf(total: number): { tier: string; color: string } {
  for (const t of TIERS) {
    if (total < t.max) return { tier: t.tier, color: t.color }
  }
  return { tier: 'Adam / Eve', color: 'tier-red' }
}

const round1 = (v: number): number => Math.round(v * 10) / 10

/** 照片质量→presentation 子分（同 sub-scale，封顶 ~peak，避免照片质量膨胀总分）。 */
const PRESENTATION_PEAK = DEFAULT_PEAK
/** 人群典型照片质量，用于 deltas 反事实基线。 */
const POP_PHOTO_AVG = 0.75

/** 某 source 的反事实基线子分（人群典型值下的 sub01）。 */
function avgSub01(source: GeoMetricKey | 'photo_quality', gender: ScorerInput['gender']): number {
  if (source === 'photo_quality') return PRESENTATION_PEAK * POP_PHOTO_AVG
  const popAvg = PARAMS.metrics[source].pop_avg
  if (popAvg == null) return 0
  return scoreMetric(source, popAvg, gender).sub
}

export function scoreFromLandmarks(input: ScorerInput): ScoreReport {
  const { landmarks, gender, genderSource, pose, photoQuality, createdAt } = input
  const metrics = computeMetrics(landmarks)

  // 各 geo 指标 → [0, peak] 子分
  const metricSub: Record<string, { sub01: number; value: number; label: string; ideal: string }> = {}
  for (const key of Object.keys(metrics) as GeoMetricKey[]) {
    const ms = scoreMetric(key, metrics[key], gender)
    metricSub[key] = { sub01: ms.sub, value: ms.value, label: ms.label, ideal: ms.ideal }
  }

  const photoSub01 = PRESENTATION_PEAK * clamp01(photoQuality)
  const rarity = PARAMS.rarity
  const nearPeakThreshold = rarity.near_peak_ratio * DEFAULT_PEAK

  // 8 子指标（0..8），子分 = 来源 sub01 均值
  const subScores: Record<string, number> = {}
  const sub01ByKey: Record<string, number> = {}
  let nNearPeak = 0
  let nTerrible = 0
  for (const sub of SUBS) {
    const sourceSubs = sub.sources.map((s) => (s === 'photo_quality' ? photoSub01 : metricSub[s].sub01))
    const sub01 = mean(sourceSubs)
    sub01ByKey[sub.key] = sub01
    subScores[sub.key] = round1(sub01 * 8)
    if (sub01 >= nearPeakThreshold) nNearPeak++
    if (sub01 < rarity.terrible_threshold) nTerrible++
  }

  // 4 主类（0..8）= 其下子指标均值
  const mainScores = { harmony: 0, dimorphism: 0, angularity: 0, presentation: 0 } as Record<MainCategory, number>
  for (const main of ['harmony', 'dimorphism', 'angularity', 'presentation'] as MainCategory[]) {
    const subsInMain = SUBS.filter((s) => s.main === main)
    mainScores[main] = round1(mean(subsInMain.map((s) => sub01ByKey[s.key] * 8)))
  }

  // base = Σ w·main；coherence_bonus = B·(nNearPeak/8)²·(1 − nTerrible/2)
  //  - (nNearPeak/8)²：只有"多数子指标接近 peak"才显著奖励
  //  - (1 − nTerrible/2)：2 项严重缺陷即清零 bonus——"有好下颌但三庭/中面崩"不配拿协同奖励
  const w = PARAMS.main_weights
  const base =
    w.harmony * mainScores.harmony +
    w.dimorphism * mainScores.dimorphism +
    w.angularity * mainScores.angularity +
    w.presentation * mainScores.presentation
  const coherenceFactor = (nNearPeak / SUBS.length) ** 2 * clamp01(1 - nTerrible / 2)
  const coherenceBonus = rarity.bonus_max * coherenceFactor
  let total = Math.max(0, Math.min(8, base + coherenceBonus))
  total = round1(total)

  const conf = confidence({ pose, landmarks, photoQuality })
  const { tier, color: _color } = tierOf(total)

  // deltas：反事实基线——"若该指标处于人群典型值，总分变化量"
  // Σ impact = base − baseline_base（自洽）；coherence_bonus 不折进 per-metric deltas，单独展示
  const deltas: Delta[] = []
  for (const sub of SUBS) {
    const subsInMain = SUBS.filter((s) => s.main === sub.main).length
    const mainWeight = w[sub.main] ?? 0
    for (const s of sub.sources) {
      let value: number
      let actualSub01: number
      let label: string
      let ideal: string
      if (s === 'photo_quality') {
        value = clamp01(photoQuality)
        actualSub01 = photoSub01
        label = 'Presentation Quality'
        ideal = '≥0.8'
      } else {
        value = metricSub[s].value
        actualSub01 = metricSub[s].sub01
        label = metricSub[s].label
        ideal = metricSub[s].ideal
      }
      const avgS01 = avgSub01(s, gender)
      const impact = round1(((actualSub01 - avgS01) * 8 * mainWeight) / subsInMain / sub.sources.length)
      deltas.push({ key: s, label, value: round1(value), ideal, impact })
    }
  }

  // 强弱项：按子指标分排序
  const ranked = SUBS.map((s) => ({ def: s, score: sub01ByKey[s.key] * 8 })).sort((a, b) => b.score - a.score)
  const strengths = ranked.slice(0, 3).map((r) => `${r.def.label}: strong performance (${round1(r.score)}/8)`)
  const weaknesses = ranked.slice(-3).reverse().map((r) => `${r.def.label}: room to improve (${round1(r.score)}/8)`)
  const suggestions = ranked.slice(-3).reverse().map((r) => SUGGESTIONS[r.def.key] ?? '').filter(Boolean)

  return {
    schema_version: '1.0',
    total,
    tier,
    confidence: round1(conf),
    base_total: round1(base),
    coherence_bonus: round1(coherenceBonus),
    main_scores: mainScores,
    sub_scores: subScores,
    metrics,
    deltas,
    strengths,
    weaknesses,
    suggestions,
    meta: {
      gender,
      gender_source: genderSource,
      pose,
      photo_quality: round1(clamp01(photoQuality)),
      scorer: 'rules_v0',
      created_at: createdAt,
    },
  }
}